import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { CountrySelect } from 'components/ui/country-select';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { ACCOUNT_QUERY_KEY, useCreateAccount } from 'features/profile/hooks/use-account';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useGetRolesQuery } from 'features/iam/hooks/use-iam';
import { Badge } from 'components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { useAuthStore } from 'state/store/auth/useAuthStore';

/**
 * AddUser Component
 *
 * A dialog component that displays a form for creating a new user account. On successful submission,
 * it invalidates and refetches account-related queries and reloads the page to reflect the changes.
 *
 * Features:
 * - Uses `react-hook-form` for form state and validation
 * - Submits form data via `useCreateAccount` mutation
 * - Auto-refreshes relevant queries and reloads the window on success
 * - Provides input fields for first name, last name, email, and organization selection
 * - Organization autocomplete with search functionality
 * - Styled using Tailwind and ShadCN dialog components
 *
 * Props:
 * - `onClose` (function): Callback to close the dialog
 *
 * @param {AddUserProps} props - Component props
 * @returns {JSX.Element} A modal with a user invitation form
 *
 * @example
 * <AddUser onClose={() => setIsDialogOpen(false)} />
 */

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  salutation: string;
  organizationId: string;
  roles: string[];
  currentRole: string;
}

type AddUserProps = {
  onClose: () => void;
};

const MAX_ROLES = 3;
const HIDDEN_ROLE_NAMES = new Set(['cloudadmin']);
const ALLOWED_ADD_USER_ROLE_NAMES: Set<string> = new Set([ROLES.USER, ROLES.ADMIN]);

export const AddUser: React.FC<AddUserProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUser = useCommonStore((state) => state.user);
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );


  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          return ALLOWED_ADD_USER_ROLE_NAMES.has(role.name);
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  const { mutate: createAccount } = useCreateAccount({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });

      await queryClient.refetchQueries({
        queryKey: ACCOUNT_QUERY_KEY,
        type: 'active',
        exact: false,
      });

      onClose();
      window.location.reload();
    },
  });

  const form = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      country: '',
      salutation: '',
      organizationId: '',
      roles: [],
      currentRole: '',
    },
  });

  const { control, handleSubmit, setValue, watch } = form;
  const selectedOrganizationId = watch('organizationId');
  const selectedOrganization = organizations.find(org => org.itemId === selectedOrganizationId);
  const watchedValues = watch();
  const isOrgAdminOrUser = watchedValues.roles?.includes(ROLES.ADMIN) || watchedValues.roles?.includes(ROLES.USER);
  const currentMembershipOrganizationId =
    selectedOrgId ||
    currentUser?.organizationId ||
    'default';


  const onSubmit = async (data: FormData) => {
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: data.country,
      userPassType: 1,
      userCreationType: 1,
      platform: 'blocks_portal',
      organizationId: currentMembershipOrganizationId,
      organizationIds: isAdmin ? (data.organizationId == "" ? [] : [data.organizationId]) : organizationsData?.data?.map((org) => org.itemId),
      roles: data.roles ?? [],
      permissions: [],
      // memberships: [
      //   {
      //     organizationId: currentMembershipOrganizationId,
      //     roles: data.roles ?? [],
      //   }
      // ],
    };

    createAccount(payload);
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    const currentRoles = form.getValues('roles') || [];

    if (currentRoles.length >= MAX_ROLES) return;

    if (!currentRoles.includes(roleSlug)) {
      setValue('roles', [...currentRoles, roleSlug]);
    }
    setValue('currentRole', '');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    const currentRoles = form.getValues('roles') || [];
    setValue(
      'roles',
      currentRoles.filter((role) => role !== roleToRemove)
    );
  };

  const getAvailableRoles = () => {
    const selectedRoles = form.getValues('roles') || [];
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  /* const getRoleNameBySlug = (slug: string) => {
    const role = availableRoles.find((r) => r.slug === slug);
    return role ? role.name : slug;
  }; */

  const isMaxRolesReached = () => {
    const selectedRoles = form.getValues('roles') || [];
    return selectedRoles.length >= MAX_ROLES;
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <DialogContent
      className=" sm:max-w-[500px] overflow-y-auto max-h-screen"
      onClick={handleDialogClick}
      hideClose
    >
      <DialogHeader className="pr-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <DialogTitle className="mb-2">{t('ADD_USER')}</DialogTitle>
            <DialogDescription className="text-medium-emphasis font-normal">
              {t('ADD_USER_DESCRIPTION')}
            </DialogDescription>
          </div>
          <button
            type="button"
            aria-label={t('CLOSE') || 'Close'}
            onClick={onClose}
            className="inline-flex h-4 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4">
            <FormField
              control={control}
              name="firstName"
              rules={{
                required: t('FIRST_NAME_REQUIRED'),
                maxLength: {
                  value: 15,
                  message: t('MAX_15_CHARACTERS')
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('FIRST_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_FIRST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="lastName"
              rules={{
                required: t('LAST_NAME_REQUIRED'),
                maxLength: {
                  value: 15,
                  message: t('MAX_15_CHARACTERS')
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('LAST_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_LAST_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="email"
              rules={{ required: t('EMAIL_REQUIRED'), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('ENTER_VALID_EMAIL') } }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}*</Label>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('ENTER_YOUR_EMAIL_ADDRESS')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="currentRole"
              rules={{ required: watchedValues.roles.length > 0 ? false : t('ROLES_REQUIRED') }}
              render={({ field }) => (
                <FormItem>
                  <Label>
                    {t('ROLES')} {isOrgAdmin ? "*" : ""}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      onValueChange={(value) => {
                        if (value === ROLES.ADMIN && isAdmin) {
                          setValue('organizationId', '');
                        }
                        handleAddRole(value);
                      }}
                      value={field.value}
                      disabled={isLoadingRoles || isMaxRolesReached()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingRoles
                                ? `${t('LOADING_ROLES')}...`
                                : isMaxRolesReached()
                                  ? t('MAX_ROLES_REACHED')
                                  : t('SELECT_ROLES')
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableRoles().map((role) => (
                          <SelectItem key={role.slug} value={role.slug}>
                            <span>{t(role.slug?.toUpperCase())}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {watchedValues.roles?.map((roleSlug) => (
                        <Badge
                          key={roleSlug}
                          variant="secondary"
                          className="flex items-center gap-1 border-border bg-muted pr-1 text-foreground hover:bg-muted/80 dark:border-white/15 dark:bg-white/10 dark:text-foreground dark:hover:bg-white/15"
                        >
                          <span>{t(roleSlug?.toUpperCase())}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-4 w-4 p-0 text-foreground hover:bg-white/10 hover:text-foreground dark:text-foreground dark:hover:bg-white/10"
                            onClick={() => handleRemoveRole(roleSlug)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose}>
              {t('CANCEL')}
            </Button>
            <Button type="submit">{t('INVITE_USER')}</Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
